import { useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { PageHeader, Section, StatBadge } from "@/components/Chrome";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { Trash } from "@phosphor-icons/react";

export default function Memory() {
  const qc = useQueryClient();
  const { data: items = [] } = useQuery({ queryKey: ["memory"], queryFn: api.memory.list });
  const [form, setForm] = useState({ kind: "preference", key: "", value: "" });

  const create = useMutation({
    mutationFn: (data) => api.memory.create(data),
    onSuccess: () => { qc.invalidateQueries({ queryKey: ["memory"] }); toast.success("Memory added"); setForm({ kind: "preference", key: "", value: "" }); },
  });
  const remove = useMutation({
    mutationFn: (id) => api.memory.remove(id),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["memory"] }),
  });

  return (
    <div data-testid="memory-page">
      <PageHeader
        eyebrow="Long-term memory"
        title="What Copilot knows about you"
        subtitle="Preferences, entities and decisions Copilot recalls before every action. Copilot also auto-captures any message that starts with “remember:”."
      />

      <Section title="Add memory">
        <div className="grid grid-cols-1 md:grid-cols-4 gap-2 max-w-3xl">
          <select
            data-testid="memory-kind"
            value={form.kind}
            onChange={(e) => setForm({ ...form, kind: e.target.value })}
            className="bg-stone-900 border border-stone-800 px-3 py-2 text-sm text-stone-100 focus:border-orange-600 outline-none"
          >
            <option value="preference">preference</option>
            <option value="entity">entity</option>
            <option value="decision">decision</option>
            <option value="fact">fact</option>
          </select>
          <input
            data-testid="memory-key"
            placeholder="key (e.g. investor CC)"
            value={form.key}
            onChange={(e) => setForm({ ...form, key: e.target.value })}
            className="bg-stone-900 border border-stone-800 px-3 py-2 text-sm text-stone-100 focus:border-orange-600 outline-none"
          />
          <input
            data-testid="memory-value"
            placeholder="value"
            value={form.value}
            onChange={(e) => setForm({ ...form, value: e.target.value })}
            className="bg-stone-900 border border-stone-800 px-3 py-2 text-sm text-stone-100 focus:border-orange-600 outline-none md:col-span-1"
          />
          <button
            data-testid="memory-add"
            onClick={() => form.key && form.value && create.mutate(form)}
            className="bg-orange-600 hover:bg-orange-500 text-white text-xs uppercase tracking-widest font-mono px-4 py-2 transition-colors">
            Save
          </button>
        </div>
      </Section>

      <Section title={`Stored (${items.length})`}>
        {items.length === 0 ? (
          <div className="text-sm text-stone-500">No memory yet.</div>
        ) : (
          <ul className="divide-y divide-stone-800/70" data-testid="memory-list">
            {items.map((m) => (
              <li key={m.id} className="py-3 grid grid-cols-12 items-center gap-4" data-testid={`memory-${m.id}`}>
                <div className="col-span-2"><StatBadge status="info">{m.kind}</StatBadge></div>
                <div className="col-span-3 text-sm text-stone-200 font-mono">{m.key}</div>
                <div className="col-span-6 text-sm text-stone-400">{m.value}</div>
                <div className="col-span-1 text-right">
                  <button data-testid={`memory-remove-${m.id}`}
                    onClick={() => remove.mutate(m.id)}
                    className="text-stone-500 hover:text-red-500 transition-colors">
                    <Trash size={14} />
                  </button>
                </div>
              </li>
            ))}
          </ul>
        )}
      </Section>
    </div>
  );
}
